import type { MaxMatchMiniMr } from '@/entities/max-match/api/max-match.api';
import type {
  DrugMatchingResultItem,
} from '@/pages/prescriptions/prescriptionStagingUtils';
import type { EditedOcrDrugRow } from '@/pages/prescriptions/prescriptionOcrUtils';

export const createTempRowKey = (prefix: string): string =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const withMiniTempRowKey = (item: MaxMatchMiniMr): MaxMatchMiniMr =>
  item.mrId != null
    ? item
    : {
        ...item,
        __tempRowKey: item.__tempRowKey ?? createTempRowKey('ocr'),
      };

export const mapOcrLinesToMrs = (
  lines: {
    drugCode: string;
    drugName?: string;
    outpatientUsageAmount?: number;
    inHouseUsageAmount?: number;
  }[],
): MaxMatchMiniMr[] =>
  lines.map((item) =>
    withMiniTempRowKey({
      mrId: undefined,
      drugCode: item.drugCode,
      drugName: item.drugName,
      outHospitalQuantity: item.outpatientUsageAmount ?? 0,
      inHospitalQuantity: item.inHouseUsageAmount ?? 0,
      hasDrugInfo: null,
    }),
  );

export const mapEditedOcrRowsToMrs = (
  rows: EditedOcrDrugRow[],
): MaxMatchMiniMr[] =>
  rows.map((row) =>
    withMiniTempRowKey({
      mrId: undefined,
      drugCode: row.drugCode ?? '',
      drugName: row.drugName ?? undefined,
      outHospitalQuantity: row.outHospitalQuantity,
      inHospitalQuantity: row.inHospitalQuantity,
      price: row.price ?? undefined,
      pharmaCompanyName: row.pharmaCompanyName ?? undefined,
      hasDrugInfo: null,
    }),
  );

export const getMrRowKey = (record: MaxMatchMiniMr): string | number =>
  record.mrId ?? record.__tempRowKey ?? record.drugCode;

export const formatMiniAmount = (value?: number): string =>
  (value ?? 0).toLocaleString();

export const calcMrTotalAmount = (record: MaxMatchMiniMr): number =>
  (record.price ?? 0) *
  ((record.outHospitalQuantity ?? 0) + (record.inHospitalQuantity ?? 0));

/** 같은 drugCode 행을 1건으로 합친다 (레거시 mergeDrugsByCode) */
export const mergeDrugsByCode = (drugs: MaxMatchMiniMr[]): MaxMatchMiniMr[] => {
  const merged = new Map<string, MaxMatchMiniMr>();
  drugs.forEach((drug) => {
    const existing = merged.get(drug.drugCode);
    if (!existing) {
      merged.set(drug.drugCode, { ...drug });
      return;
    }
    existing.outHospitalQuantity =
      (existing.outHospitalQuantity || 0) + (drug.outHospitalQuantity || 0);
    existing.inHospitalQuantity =
      (existing.inHospitalQuantity || 0) + (drug.inHospitalQuantity || 0);
  });
  return Array.from(merged.values());
};

export interface DuplicatedDrugGroup {
  drugCode: string;
  rows: MaxMatchMiniMr[];
  drugName?: string;
  pharmaCompanyName?: string;
  price?: number;
  totalOutHospitalQuantity: number;
  totalInHospitalQuantity: number;
}

/** 미저장 행 중 drugCode가 2건 이상 겹치는 그룹 */
export const getDuplicatedDrugGroups = (
  drugData: MaxMatchMiniMr[],
): DuplicatedDrugGroup[] => {
  const groups = new Map<string, MaxMatchMiniMr[]>();
  drugData
    .filter((item) => item.mrId == null && item.drugCode)
    .forEach((item) => {
      const rows = groups.get(item.drugCode) ?? [];
      rows.push(item);
      groups.set(item.drugCode, rows);
    });

  return Array.from(groups.entries())
    .filter(([, rows]) => rows.length > 1)
    .map(([drugCode, rows]) => ({
      drugCode,
      rows,
      drugName: rows[0]?.drugName,
      pharmaCompanyName: rows[0]?.pharmaCompanyName,
      price: rows[0]?.price,
      totalOutHospitalQuantity: rows.reduce(
        (sum, row) => sum + (row.outHospitalQuantity || 0),
        0,
      ),
      totalInHospitalQuantity: rows.reduce(
        (sum, row) => sum + (row.inHospitalQuantity || 0),
        0,
      ),
    }));
};

export const applyMiniDrugMatchingResults = (
  rows: MaxMatchMiniMr[],
  results: DrugMatchingResultItem[],
): { nextList: MaxMatchMiniMr[]; matchedCount: number } => {
  let matchedCount = 0;
  const nextList = rows.map((row, index) => {
    const top = results[index]?.candidates?.[0];
    if (!top) return row;
    matchedCount += 1;
    return {
      ...row,
      drugCode: top.drugCode ?? row.drugCode,
      drugName: top.drugName ?? row.drugName,
      price:
        top.priceHira != null && top.priceHira > 0 ? top.priceHira : row.price,
      pharmaCompanyName: top.pharmaCompanyName ?? row.pharmaCompanyName,
      // 매칭 성공 시 마스터 정보가 채워진 상태로 간주 (미저장이므로 null 유지)
      hasDrugInfo: row.mrId == null ? null : true,
    };
  });
  return { nextList, matchedCount };
};
